/**
 * Company Service
 * Handles company-specific operations like job postings, applications management, etc.
 */

import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  startAfter,
  arrayUnion,
  arrayRemove,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';

/**
 * Create a new job posting
 * 
 * @param {Object} jobData - Job posting data
 * @param {string} companyId - Company ID
 * @returns {Promise<string>} Job posting ID
 */
export const createJobPosting = async (jobData, companyId) => {
  try {
    const job = {
      ...jobData,
      employerId: companyId,
      status: 'active',
      applicationCount: 0,
      viewCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      expiresAt: jobData.expiresAt || null
    };
    
    const docRef = await addDoc(collection(db, 'jobPostings'), job);
    
    // Update company stats
    await updateCompanyStats(companyId);
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating job posting:', error);
    throw error;
  }
};

/**
 * Get job postings for a company
 * 
 * @param {string} companyId - Company ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of job postings
 */
export const getCompanyJobPostings = async (companyId, options = {}) => {
  try {
    const { 
      status = null, 
      limitCount = 20, 
      lastDoc = null,
      orderField = 'createdAt',
      orderDirection = 'desc' 
    } = options;

    let jobsQuery = query(
      collection(db, 'jobPostings'),
      where('employerId', '==', companyId),
      orderBy(orderField, orderDirection)
    );

    if (status) {
      jobsQuery = query(jobsQuery, where('status', '==', status));
    }

    if (lastDoc) {
      jobsQuery = query(jobsQuery, startAfter(lastDoc));
    }
    
    jobsQuery = query(jobsQuery, limit(limitCount));

    const snapshot = await getDocs(jobsQuery);
    const jobs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt instanceof Timestamp ? 
        doc.data().createdAt.toDate() : 
        new Date(doc.data().createdAt)
    }));

    return {
      jobs,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error getting company job postings:', error);
    throw error;
  }
};

/**
 * Update job posting
 * 
 * @param {string} jobId - Job posting ID
 * @param {Object} updates - Updates to apply
 * @param {string} companyId - Company ID (for verification)
 * @returns {Promise<void>}
 */
export const updateJobPosting = async (jobId, updates, companyId) => {
  try {
    // Verify ownership
    const jobRef = doc(db, 'jobPostings', jobId);
    const jobSnap = await getDoc(jobRef);
    
    if (!jobSnap.exists()) {
      throw new Error('Job posting not found');
    }
    
    if (jobSnap.data().employerId !== companyId) {
      throw new Error('Unauthorized: Cannot update job posting');
    }
    
    await updateDoc(jobRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    
    // Update company stats if status changed
    if (updates.status) {
      await updateCompanyStats(companyId);
    }
  } catch (error) {
    console.error('Error updating job posting:', error);
    throw error;
  }
};

/**
 * Delete job posting
 * 
 * @param {string} jobId - Job posting ID
 * @param {string} companyId - Company ID (for verification)
 * @returns {Promise<void>}
 */
export const deleteJobPosting = async (jobId, companyId) => {
  try {
    // Verify ownership
    const jobRef = doc(db, 'jobPostings', jobId);
    const jobSnap = await getDoc(jobRef);
    
    if (!jobSnap.exists()) {
      throw new Error('Job posting not found');
    }
    
    if (jobSnap.data().employerId !== companyId) {
      throw new Error('Unauthorized: Cannot delete job posting');
    }
    
    // Soft delete by updating status
    await updateDoc(jobRef, {
      status: 'deleted',
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Update company stats
    await updateCompanyStats(companyId);
  } catch (error) {
    console.error('Error deleting job posting:', error);
    throw error;
  }
};

/**
 * Get applications for company's job postings
 * 
 * @param {string} companyId - Company ID
 * @param {Object} options - Query options
 * @returns {Promise<Array>} Array of applications with job details
 */
export const getCompanyApplications = async (companyId, options = {}) => {
  try {
    const { 
      status = null, 
      jobId = null,
      limitCount = 50, 
      lastDoc = null 
    } = options;

    // First get all job IDs for this company
    const jobsQuery = query(
      collection(db, 'jobPostings'),
      where('employerId', '==', companyId)
    );
    const jobsSnapshot = await getDocs(jobsQuery);
    const jobIds = jobsSnapshot.docs.map(doc => doc.id);
    
    if (jobIds.length === 0) {
      return { applications: [], hasMore: false, lastDoc: null };
    }

    // Build applications query
    let applicationsQuery = query(
      collection(db, 'jobApplications'),
      where('jobId', 'in', jobIds.slice(0, 10)), // Firestore limit for 'in' queries
      orderBy('appliedAt', 'desc')
    );

    if (status) {
      applicationsQuery = query(applicationsQuery, where('status', '==', status));
    }

    if (jobId) {
      applicationsQuery = query(
        collection(db, 'jobApplications'),
        where('jobId', '==', jobId),
        orderBy('appliedAt', 'desc')
      );
      if (status) {
        applicationsQuery = query(applicationsQuery, where('status', '==', status));
      }
    }

    if (lastDoc) {
      applicationsQuery = query(applicationsQuery, startAfter(lastDoc));
    }
    
    applicationsQuery = query(applicationsQuery, limit(limitCount));

    const snapshot = await getDocs(applicationsQuery);
    
    // Get job details and applicant details for each application
    const applications = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const appData = doc.data();
        
        // Get job details
        const jobRef = doc(db, 'jobPostings', appData.jobId);
        const jobSnap = await getDoc(jobRef);
        const jobData = jobSnap.exists() ? jobSnap.data() : null;
        
        // Get applicant profile (basic info only)
        const profileRef = doc(db, 'profiles', appData.userId);
        const profileSnap = await getDoc(profileRef);
        const applicantData = profileSnap.exists() ? {
          name: profileSnap.data().basicInfo?.name,
          email: profileSnap.data().basicInfo?.email,
          university: profileSnap.data().basicInfo?.university,
          profilePhoto: profileSnap.data().photos?.[0] || null
        } : null;
        
        return {
          id: doc.id,
          ...appData,
          jobDetails: jobData,
          applicantDetails: applicantData,
          appliedAt: appData.appliedAt instanceof Timestamp ? 
            appData.appliedAt.toDate() : 
            new Date(appData.appliedAt)
        };
      })
    );
    
    return {
      applications,
      lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error getting company applications:', error);
    throw error;
  }
};

/**
 * Update application status
 * 
 * @param {string} applicationId - Application ID
 * @param {string} newStatus - New application status
 * @param {string} message - Optional message
 * @param {string} companyId - Company ID (for verification)
 * @returns {Promise<void>}
 */
export const updateApplicationStatus = async (applicationId, newStatus, message = '', companyId) => {
  try {
    const applicationRef = doc(db, 'jobApplications', applicationId);
    const applicationSnap = await getDoc(applicationRef);
    
    if (!applicationSnap.exists()) {
      throw new Error('Application not found');
    }
    
    const applicationData = applicationSnap.data();
    
    // Verify company owns the job
    const jobRef = doc(db, 'jobPostings', applicationData.jobId);
    const jobSnap = await getDoc(jobRef);
    
    if (!jobSnap.exists() || jobSnap.data().employerId !== companyId) {
      throw new Error('Unauthorized: Cannot update this application');
    }
    
    // Update application status
    await updateDoc(applicationRef, {
      status: newStatus,
      statusMessage: message,
      statusUpdatedAt: serverTimestamp(),
      statusUpdatedBy: companyId,
      updatedAt: serverTimestamp()
    });
    
    // Create notification for applicant
    await createApplicationNotification(
      applicationData.userId,
      applicationId,
      newStatus,
      jobSnap.data().title,
      message
    );
    
    // Update company stats
    await updateCompanyStats(companyId);
  } catch (error) {
    console.error('Error updating application status:', error);
    throw error;
  }
};

/**
 * Get company dashboard statistics
 * 
 * @param {string} companyId - Company ID
 * @returns {Promise<Object>} Company statistics
 */
export const getCompanyDashboardStats = async (companyId) => {
  try {
    // Get job postings stats
    const jobsQuery = query(
      collection(db, 'jobPostings'),
      where('employerId', '==', companyId)
    );
    const jobsSnapshot = await getDocs(jobsQuery);
    
    const totalJobs = jobsSnapshot.size;
    const activeJobs = jobsSnapshot.docs.filter(doc => 
      doc.data().status === 'active'
    ).length;
    
    // Get all job IDs for application stats
    const jobIds = jobsSnapshot.docs.map(doc => doc.id);
    
    let totalApplications = 0;
    let pendingApplications = 0;
    
    if (jobIds.length > 0) {
      // Get applications in batches (Firestore 'in' query limit is 10)
      const batchSize = 10;
      for (let i = 0; i < jobIds.length; i += batchSize) {
        const batchJobIds = jobIds.slice(i, i + batchSize);
        
        const applicationsQuery = query(
          collection(db, 'jobApplications'),
          where('jobId', 'in', batchJobIds)
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        
        totalApplications += applicationsSnapshot.size;
        pendingApplications += applicationsSnapshot.docs.filter(doc => 
          doc.data().status === 'pending'
        ).length;
      }
    }
    
    // Calculate additional metrics
    const averageApplicationsPerJob = totalJobs > 0 ? (totalApplications / totalJobs).toFixed(1) : 0;
    
    return {
      totalJobs,
      activeJobs,
      totalApplications,
      pendingApplications,
      averageApplicationsPerJob,
      responseRate: totalApplications > 0 ? 
        (((totalApplications - pendingApplications) / totalApplications) * 100).toFixed(1) : 0
    };
  } catch (error) {
    console.error('Error getting company dashboard stats:', error);
    throw error;
  }
};

/**
 * Search and filter applications
 * 
 * @param {string} companyId - Company ID
 * @param {Object} filters - Search filters
 * @returns {Promise<Array>} Filtered applications
 */
export const searchCompanyApplications = async (companyId, filters = {}) => {
  try {
    const { 
      searchTerm = '', 
      status = null, 
      jobId = null,
      university = null,
      dateRange = null 
    } = filters;
    
    // Get base applications
    const { applications } = await getCompanyApplications(companyId, { 
      status, 
      jobId, 
      limitCount: 100 
    });
    
    // Apply client-side filters
    let filteredApplications = applications;
    
    // Search term filter (name, email, university)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filteredApplications = filteredApplications.filter(app => 
        app.applicantDetails?.name?.toLowerCase().includes(term) ||
        app.applicantDetails?.email?.toLowerCase().includes(term) ||
        app.applicantDetails?.university?.toLowerCase().includes(term)
      );
    }
    
    // University filter
    if (university) {
      filteredApplications = filteredApplications.filter(app => 
        app.applicantDetails?.university === university
      );
    }
    
    // Date range filter
    if (dateRange) {
      const { startDate, endDate } = dateRange;
      filteredApplications = filteredApplications.filter(app => {
        const appliedDate = app.appliedAt;
        return appliedDate >= startDate && appliedDate <= endDate;
      });
    }
    
    return filteredApplications;
  } catch (error) {
    console.error('Error searching company applications:', error);
    throw error;
  }
};

/**
 * Update company stats (internal helper)
 * 
 * @param {string} companyId - Company ID
 * @returns {Promise<void>}
 */
const updateCompanyStats = async (companyId) => {
  try {
    const stats = await getCompanyDashboardStats(companyId);
    
    const statsRef = doc(db, 'companyStats', companyId);
    await updateDoc(statsRef, {
      ...stats,
      lastUpdated: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating company stats:', error);
    // Don't throw error for stats update failure
  }
};

/**
 * Create notification for application status change
 * 
 * @param {string} userId - User ID to notify
 * @param {string} applicationId - Application ID
 * @param {string} status - New status
 * @param {string} jobTitle - Job title
 * @param {string} message - Optional message
 * @returns {Promise<void>}
 */
const createApplicationNotification = async (userId, applicationId, status, jobTitle, message) => {
  try {
    const notification = {
      userId,
      type: 'job_application',
      content: {
        applicationId,
        status,
        jobTitle,
        message
      },
      isRead: false,
      createdAt: serverTimestamp()
    };
    
    await addDoc(collection(db, 'notifications'), notification);
  } catch (error) {
    console.error('Error creating application notification:', error);
    // Don't throw error for notification failure
  }
};

/**
 * Get job posting analytics
 * 
 * @param {string} jobId - Job posting ID
 * @param {string} companyId - Company ID (for verification)
 * @returns {Promise<Object>} Job analytics
 */
export const getJobAnalytics = async (jobId, companyId) => {
  try {
    // Verify ownership
    const jobRef = doc(db, 'jobPostings', jobId);
    const jobSnap = await getDoc(jobRef);
    
    if (!jobSnap.exists() || jobSnap.data().employerId !== companyId) {
      throw new Error('Unauthorized: Cannot access job analytics');
    }
    
    const jobData = jobSnap.data();
    
    // Get applications for this job
    const applicationsQuery = query(
      collection(db, 'jobApplications'),
      where('jobId', '==', jobId)
    );
    const applicationsSnapshot = await getDocs(applicationsQuery);
    
    // Calculate analytics
    const totalApplications = applicationsSnapshot.size;
    const applications = applicationsSnapshot.docs.map(doc => doc.data());
    
    const statusBreakdown = applications.reduce((acc, app) => {
      acc[app.status] = (acc[app.status] || 0) + 1;
      return acc;
    }, {});
    
    const universityBreakdown = applications.reduce((acc, app) => {
      // Would need to fetch applicant profiles for university data
      return acc;
    }, {});
    
    return {
      jobTitle: jobData.title,
      totalViews: jobData.viewCount || 0,
      totalApplications,
      statusBreakdown,
      universityBreakdown,
      createdAt: jobData.createdAt,
      lastApplication: applications.length > 0 ? 
        Math.max(...applications.map(app => app.appliedAt?.toDate?.() || new Date(app.appliedAt))) : 
        null
    };
  } catch (error) {
    console.error('Error getting job analytics:', error);
    throw error;
  }
};

export default {
  createJobPosting,
  getCompanyJobPostings,
  updateJobPosting,
  deleteJobPosting,
  getCompanyApplications,
  updateApplicationStatus,
  getCompanyDashboardStats,
  searchCompanyApplications,
  getJobAnalytics
};